import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { ZoomIn, ZoomOut } from "lucide-react";

async function getCroppedBlob(imageSrc, crop, outputSize = 512) {
  const image = new Image();
  image.src = imageSrc;
  await new Promise((res, rej) => { image.onload = res; image.onerror = rej; });
  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    crop.x, crop.y, crop.width, crop.height,
    0, 0, outputSize, outputSize
  );
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/png", 0.92);
  });
}

export default function ImageCropDialog({ open, onOpenChange, imageSrc, onConfirm, title = "Ajusta tu logo", description = "Mueve y haz zoom. El círculo muestra cómo se verá." }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixelArea, setPixelArea] = useState(null);
  const [loading, setLoading] = useState(false);

  const onCropComplete = useCallback((_area, pixelArea) => {
    setPixelArea(pixelArea);
  }, []);

  const confirm = async () => {
    if (!pixelArea || !imageSrc) return;
    setLoading(true);
    try {
      const blob = await getCroppedBlob(imageSrc, pixelArea);
      await onConfirm(blob);
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="relative w-full h-72 bg-neutral-900 rounded-xl overflow-hidden" data-testid="crop-area">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>
        <div className="flex items-center gap-3">
          <ZoomOut size={16} className="text-neutral-500" />
          <input
            type="range" min="1" max="4" step="0.01"
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-black"
            data-testid="crop-zoom"
          />
          <ZoomIn size={16} className="text-neutral-500" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} data-testid="crop-cancel">Cancelar</Button>
          <Button className="flex-1 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90" onClick={confirm} disabled={loading || !pixelArea} data-testid="crop-confirm">
            {loading ? "Subiendo..." : "Usar esta imagen"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
